import { useCallback } from 'react';
import { QueryParameters } from '@browsing/utils';
/**
 * Schedule a microtask to ensure other functions within the same task execute before navigating.
 *
 * Example
 * ```
 * const onClick = () => {
 *   navigateToPDP() // <- navigation scheduled
 *   trackClick()    // <- executed
 * }                 // <- end of task, navigation occurs
 * ```
 */
var defaultNavigateToPDPInCurrentTab = function (pdpURL) {
    Promise.resolve().then(function () {
        window.open(pdpURL, '_self');
    });
};
/**
 * Standard function to navigate to the PDP.
 *
 * - Don't use window.open yourself, and don't use react-router's `navigate` yourself.
 * - Don't rely on <a href="pdpURL">, i.e. prevent its default behavior.
 *
 * The solution is to use the `navigateToPDP()` function returned by the current hook, which will
 * navigate to the PDP in the current tab or in a new tab depending on whether `shouldOpenInNewTab`
 * is enabled.
 */
export var useNavigateToPDP = function (options) {
    var _a;
    var navigateToPDPInCurrentTab = (_a = options === null || options === void 0 ? void 0 : options.navigateToPDPInCurrentTab) !== null && _a !== void 0 ? _a : defaultNavigateToPDPInCurrentTab;
    /**
     * Get the url path to the PDP.
     *
     * Important! historically, this function was placed in the global scope, allowing both catalog
     * and mylounge applications to navigate to the pdp using its returned path. But It is now only
     * accessible from the returned value of the current hook, which should be the only way to navigate
     * to the PDP. So, don't move it back to the global scope to prevent development errors.
     */
    var getPDPURLPath = useCallback(function (options) {
        var qp = options.queryParams;
        var path = "/campaigns/".concat(qp.campaignId, "/articles/").concat(qp.configSku);
        var params = new URLSearchParams();
        [
            [QueryParameters.NavigationSource, qp.navigationSource],
            [QueryParameters.CatalogType, qp.catalogType],
            [QueryParameters.SearchAnonId, qp.searchAnonId],
            [QueryParameters.ThemeHash, qp.themeHash],
            [QueryParameters.Reco, qp.recommendedBy],
        ].forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (value) {
                params.set(key, value);
            }
        });
        var queryString = params.toString();
        return queryString ? "".concat(path, "?").concat(queryString) : path;
    }, []);
    /**
     * Important considerations when using `navigateToPDP`:
     *
     * - Calling `navigateToPDP()` is not enough. Ensure your component, whose click navigates to the
     *   PDP, is wrapped in a link <a/> whose href is set to the same pdp URL that we navigate to here.
     *   You can construct this URL using the returned `useNavigateToPDP().getPDPURLPath()` tool. This
     *   will allow the user to right-click on the link and open a context menu to navigate to the PDP
     *   in their desired way e.g. in a new tab, a new window, a new split tab, incognito mode, etc.
     *   regardless of how you used this function.
     *
     * - Browsers block calls to `navigateToPDP()` as a popup unless It is triggered by a "trusted user gesture"
     *   e.g. a link click whose listener calls navigateToPDP() -> trusted.
     *
     * - Browsers interpret linkClickEvent.preventDefault() as "prevent the navigation", but preserve
     *   the user intent in all circumstances, without letting us override it.
     *
     *   Example: a user clicks a link, and your listener calls e.preventDefault() and navigateToPDP({ shouldOpenInNewTab: true }),
     *   the final outcome will be determined by the user intent:
     *   - e.g. no modifier key     -> new tab with focus
     *   - e.g. Mac-option pressed  -> new tab with focus
     *   - e.g. Mac-command pressed -> new tab without focus
     *   - e.g. Mac-shift pressed   -> new window with focus
     *   - e.g. Mac-control pressed -> context menu opened instead
     *
     *   Example: a user clicks a link, and your listener calls e.preventDefault() and navigateToPDP({ shouldOpenInNewTab: false }),
     *   the final outcome will be determined by the user intent:
     *   - e.g. no modifier key                  -> current tab with focus
     *   - e.g. Mac-option/command/shift/control -> same as before !
     *
     *   So, our option `shouldOpenInNewTab` determines the default intent when no modifier keys are pressed.
     */
    var navigateToPDP = useCallback(function (options) {
        var _a = options.shouldOpenInNewTab, shouldOpenInNewTab = _a === void 0 ? false : _a, queryParams = options.queryParams;
        var url = getPDPURLPath({ queryParams: queryParams });
        if (shouldOpenInNewTab) {
            window.open(url, '_blank', 'noopener');
        }
        else {
            navigateToPDPInCurrentTab(url);
        }
    }, [getPDPURLPath, navigateToPDPInCurrentTab]);
    return { navigateToPDP: navigateToPDP, getPDPURLPath: getPDPURLPath };
};
